/**
 * Sealed product catalog + pricing queries against the TCGCSV-synced tables.
 *
 * Reads only — the daily refresh job (supabase/functions/sync-tcgcsv) handles
 * writes. The browser never talks to tcgcsv.com directly.
 *
 * Tables this module reads:
 *   - sealed_products  (catalog: productId, name, image, kind, etc.)
 *   - sealed_groups    (sets — for filter dropdowns)
 *   - sealed_prices    (daily price snapshots; primary key is product+date+subType)
 *   - sealed_current_prices (view: latest snapshot per product, latest day only)
 */
import { sb } from "./supabase";

export interface SealedProductRow {
  product_id:   number;
  group_id:     number;
  category_id:  number;
  name:         string;
  clean_name:   string | null;
  image_url:    string | null;
  url:          string | null;
  product_kind: string | null;
  // joined fields from sealed_current_prices (left join)
  market_price?: number | null;
  low_price?:    number | null;
  mid_price?:    number | null;
  // joined from sealed_groups
  group_name?:        string;
  group_abbreviation?: string | null;
}

export interface SealedPriceSnapshot {
  product_id:    number;
  snapshot_date: string;   // YYYY-MM-DD
  sub_type_name: string;
  market_price:  number | null;
  low_price:     number | null;
  mid_price:     number | null;
}

/** Search the catalog. Returns rows with current prices already joined.
 *  Filters: free-text name match (trigram), optional categoryId, optional kind.
 *
 *  Note: PostgREST trigram search needs `name.ilike.%query%`; we hit the GIN
 *  index for fast lookups across thousands of products. */
export async function searchSealedProducts(opts: {
  query?: string;
  categoryId?: number;
  groupId?: number;
  kind?: string;
  limit?: number;
}): Promise<SealedProductRow[]> {
  const limit = Math.min(opts.limit || 60, 200);

  // We grab raw products then merge prices client-side because PostgREST
  // doesn't expose lateral joins to views cleanly. Two queries is fine for
  // a 60-row UI grid.
  let q = sb.from("sealed_products")
    .select("product_id, group_id, category_id, name, clean_name, image_url, url, product_kind, sealed_groups(name, abbreviation)")
    .limit(limit);

  if (opts.query)      q = q.ilike("name", `%${opts.query}%`);
  if (opts.categoryId) q = q.eq("category_id", opts.categoryId);
  if (opts.groupId)    q = q.eq("group_id", opts.groupId);
  if (opts.kind)       q = q.eq("product_kind", opts.kind);

  const { data, error } = await q;
  if (error) { console.error("[searchSealedProducts]", error); return []; }
  const products = (data || []) as any[];
  if (!products.length) return [];

  // Pull current prices for these product IDs in one round-trip
  const pids = products.map(p => p.product_id);
  const { data: prices, error: pe } = await sb.from("sealed_current_prices")
    .select("product_id, sub_type_name, market_price, low_price, mid_price, snapshot_date")
    .in("product_id", pids);
  if (pe) console.warn("[searchSealedProducts] prices fetch failed:", pe);

  // Pick "Unopened" if present, else the first available subType for each product
  const priceByPid = new Map<number, any>();
  for (const p of (prices || [])) {
    const existing = priceByPid.get(p.product_id);
    if (!existing || p.sub_type_name === "Unopened") priceByPid.set(p.product_id, p);
  }

  return products.map(p => {
    const pp = priceByPid.get(p.product_id);
    const grp = p.sealed_groups || {};
    return {
      product_id:   p.product_id,
      group_id:     p.group_id,
      category_id:  p.category_id,
      name:         p.name,
      clean_name:   p.clean_name,
      image_url:    p.image_url,
      url:          p.url,
      product_kind: p.product_kind,
      market_price: pp?.market_price ?? null,
      low_price:    pp?.low_price ?? null,
      mid_price:    pp?.mid_price ?? null,
      group_name:        grp.name || "",
      group_abbreviation: grp.abbreviation || null,
    } as SealedProductRow;
  });
}

/** Single-product current price lookup. Used by FMV refresh once a lot is
 *  linked via tcgplayer_product_id. */
export async function getCurrentSealedPrice(productId: number): Promise<{ market: number | null; low: number | null; mid: number | null; date: string | null }> {
  const { data, error } = await sb.from("sealed_current_prices")
    .select("market_price, low_price, mid_price, snapshot_date, sub_type_name")
    .eq("product_id", productId);
  if (error) { console.error("[getCurrentSealedPrice]", error); return { market: null, low: null, mid: null, date: null }; }
  const rows = (data || []) as any[];
  if (!rows.length) return { market: null, low: null, mid: null, date: null };
  // Prefer Unopened; otherwise first row
  const pick = rows.find(r => r.sub_type_name === "Unopened") || rows[0];
  return {
    market: pick.market_price ?? null,
    low:    pick.low_price    ?? null,
    mid:    pick.mid_price    ?? null,
    date:   pick.snapshot_date ?? null,
  };
}

/** Pull price history for a product over the last N days. Used by the lot
 *  history price-chart widget. */
export async function getSealedPriceHistory(productId: number, days = 90): Promise<SealedPriceSnapshot[]> {
  const since = new Date();
  since.setUTCDate(since.getUTCDate() - days);
  const sinceIso = since.toISOString().slice(0, 10);

  const { data, error } = await sb.from("sealed_prices")
    .select("product_id, snapshot_date, sub_type_name, market_price, low_price, mid_price")
    .eq("product_id", productId)
    .gte("snapshot_date", sinceIso)
    .order("snapshot_date", { ascending: true });
  if (error) { console.error("[getSealedPriceHistory]", error); return []; }

  const rows = (data || []) as SealedPriceSnapshot[];
  // Prefer Unopened where multiple subtypes exist on the same day
  if (rows.some(r => r.sub_type_name === "Unopened")) {
    return rows.filter(r => r.sub_type_name === "Unopened");
  }
  return rows;
}

/** Price history for a SINGLE (catalog_prices) — same shape as the sealed
 *  snapshots. Where multiple print variants exist on the same product, pick
 *  one series using the same priority ladder the nightly revaluation uses. */
export async function getCatalogPriceHistory(productId: number, days = 90): Promise<SealedPriceSnapshot[]> {
  const since = new Date();
  since.setUTCDate(since.getUTCDate() - days);
  const sinceIso = since.toISOString().slice(0, 10);

  const { data, error } = await sb.from("catalog_prices")
    .select("product_id, snapshot_date, sub_type_name, market_price, low_price, mid_price")
    .eq("product_id", productId)
    .gte("snapshot_date", sinceIso)
    .order("snapshot_date", { ascending: true });
  if (error) { console.error("[getCatalogPriceHistory]", error); return []; }

  const rows = (data || []) as SealedPriceSnapshot[];
  if (!rows.length) return rows;
  const ladder = ["Holofoil", "Reverse Holofoil", "1st Edition Holofoil", "1st Edition Normal", "Normal", "Foil"];
  const present = new Set(rows.map(r => r.sub_type_name));
  const pick = ladder.find(v => present.has(v)) || rows[0].sub_type_name;
  return rows.filter(r => r.sub_type_name === pick);
}

/** Lightweight check — has the catalog been populated yet? Used by the picker
 *  to show a friendly "catalog not synced yet" message instead of an empty grid. */
export async function isCatalogPopulated(): Promise<boolean> {
  const { count, error } = await sb.from("sealed_products")
    .select("product_id", { count: "exact", head: true })
    .limit(1);
  if (error) return false;
  return (count || 0) > 0;
}

/** Try to guess a productId for an existing sealed lot that doesn't have one
 *  linked yet. Strategy: ilike-search product name, filter by set name if
 *  provided, prefer the single best match. Returns null if ambiguous. */
export async function tryAutoLinkSealed(productName: string, setName?: string): Promise<SealedProductRow | null> {
  const candidates = await searchSealedProducts({ query: productName, limit: 10 });
  if (!candidates.length) return null;

  if (setName) {
    const setLow = setName.toLowerCase();
    const inSet = candidates.filter(c =>
      (c.group_name || "").toLowerCase().includes(setLow) ||
      (c.group_abbreviation || "").toLowerCase() === setLow
    );
    if (inSet.length === 1) return inSet[0];
    if (inSet.length > 1) return null;  // ambiguous — let user pick
  }

  // No set hint — accept only if there's exactly one match
  return candidates.length === 1 ? candidates[0] : null;
}
